import React, { useState, useEffect } from "react";
import { X, Calendar, Clock, DollarSign, ChevronLeft, ChevronRight } from "lucide-react";
import { motion } from "framer-motion";
import { supabase } from "../../supabaseClient";

/**
 * VisitorSlotPicker - Modal for selecting a time slot for space booking
 * Shows available slots for the next 14-30 days
 */
export default function VisitorSlotPicker({ space, onClose, onSlotSelected }) {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [selectionError, setSelectionError] = useState(null);

  useEffect(() => {
    fetchAvailableSlots();
  }, [space?.id]);

  const fetchAvailableSlots = async () => {
    if (!space?.id) return;

    try {
      setLoading(true);
      console.log("[VisitorSlotPicker] Fetching slots for space:", space.id);

      const now = new Date();
      const endDate = new Date();
      endDate.setDate(now.getDate() + 30); // Next 30 days

      const { data, error } = await supabase
        .from("space_slots")
        .select("id, space_id, start_at, end_at, price_cents, currency, status")
        .eq("space_id", space.id)
        .eq("status", "open")
        .gte("start_at", now.toISOString())
        .lte("start_at", endDate.toISOString())
        .order("start_at", { ascending: true })
        .limit(100);

      if (error) {
        console.error("[VisitorSlotPicker] Error fetching slots:", {
          message: error.message,
          code: error.code,
          hint: error.hint,
        });
        setSlots([]);
      } else {
        console.log("[VisitorSlotPicker] Fetched slots:", data?.length || 0);
        setSlots(data || []);
      }
    } catch (err) {
      console.error("[VisitorSlotPicker] Unexpected error:", err);
      setSlots([]);
    } finally {
      setLoading(false);
    }
  };

  // Group slots by date
  const slotsByDate = slots.reduce((acc, slot) => {
    const date = new Date(slot.start_at).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
    if (!acc[date]) acc[date] = [];
    acc[date].push(slot);
    return acc;
  }, {});

  const dates = Object.keys(slotsByDate);

  const handleSlotClick = (slot) => {
    setSelectionError(null);

    setSelectedSlots((prev) => {
      const prevSorted = [...(prev || [])].sort(
        (a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime()
      );
      const alreadySelected = prevSorted.some((s) => s.id === slot.id);

      if (alreadySelected) {
        // Toggle off
        if (prevSorted.length <= 1) return [];

        const first = prevSorted[0];
        const last = prevSorted[prevSorted.length - 1];

        if (slot.id === first.id) return prevSorted.slice(1);
        if (slot.id === last.id) return prevSorted.slice(0, -1);

        // Middle click: collapse selection to just this slot (keeps UX simple)
        return [slot];
      }

      // Toggle on
      if (prevSorted.length === 0) return [slot];

      const first = prevSorted[0];
      const last = prevSorted[prevSorted.length - 1];
      const slotStart = new Date(slot.start_at).getTime();
      const slotEnd = new Date(slot.end_at).getTime();
      const firstStart = new Date(first.start_at).getTime();
      const firstEnd = new Date(first.end_at).getTime();
      const lastStart = new Date(last.start_at).getTime();
      const lastEnd = new Date(last.end_at).getTime();

      // Only allow extending the current contiguous block (prepend or append)
      if (slotEnd === firstStart) return [slot, ...prevSorted];
      if (slotStart === lastEnd) return [...prevSorted, slot];

      setSelectionError("Select consecutive time slots only.");
      return [slot];
    });

    console.log("[VisitorSlotPicker] Slot toggled:", {
      slotId: slot.id,
      start_at: slot.start_at,
      end_at: slot.end_at,
      price_cents: slot.price_cents,
    });
  };

  const handleConfirm = () => {
    if (!selectedSlots?.length) return;
    const sorted = [...selectedSlots].sort(
      (a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime()
    );
    console.log("[VisitorSlotPicker] Confirming slots:", sorted.map((s) => s.id));
    onSlotSelected(sorted);
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatDuration = (start, end) => {
    const startTime = new Date(start);
    const endTime = new Date(end);
    const diffMs = endTime - startTime;
    const diffHours = diffMs / (1000 * 60 * 60);
    return `${diffHours} hour${diffHours !== 1 ? "s" : ""}`;
  };

  const selectedSlotsSorted = [...(selectedSlots || [])].sort(
    (a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime()
  );
  const selectedTotalCents = selectedSlotsSorted.reduce(
    (sum, s) => sum + (Number(s.price_cents) || 0),
    0
  );
  const selectedDurationHours = selectedSlotsSorted.length
    ? (new Date(selectedSlotsSorted[selectedSlotsSorted.length - 1].end_at).getTime() -
        new Date(selectedSlotsSorted[0].start_at).getTime()) /
      (1000 * 60 * 60)
    : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="mudo-glass-panel w-full max-w-4xl max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 p-6 border-b border-white/10 bg-black/50 flex items-start justify-between">
          <div className="flex-1">
            <p className="text-xs font-semibold text-white/60 uppercase tracking-widest mb-1">
              SELECT TIME SLOT
            </p>
            <h2 className="text-2xl font-bold text-white mb-1">{space?.name}</h2>
            <p className="text-sm text-white/70">
              {space?.studio?.name || "Studio Space"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/10 transition-colors flex-shrink-0"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 px-6">
              <div className="w-12 h-12 border-4 border-white/40 border-t-transparent rounded-full animate-spin mb-4" />
              <p className="text-white/70">Loading available slots...</p>
            </div>
          ) : slots.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Calendar className="w-16 h-16 text-white/30 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">
                No Pre-Scheduled Slots Available
              </h3>
              <p className="text-white/60 text-center mb-6 max-w-md">
                This studio hasn't created public time slots yet. You can still inquire about custom booking times by contacting the studio directly.
              
                            {/* Action Buttons */}
                            <div className="flex flex-col sm:flex-row gap-3 w-full max-w-md">
                              <button
                                onClick={onClose}
                                className="flex-1 px-6 py-3 rounded-full bg-white/10 text-white font-semibold hover:bg-white/20 transition-all"
                              >
                                Close
                              </button>
                              {space?.studio?.email && (
                                <a
                                  href={`mailto:${space.studio.email}?subject=Custom Booking Request for ${space.name}`}
                                  className="flex-1 px-6 py-3 rounded-full bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold hover:from-orange-600 hover:to-orange-700 transition-all text-center"
                                >
                                  Contact Studio
                                </a>
                              )}
                            </div>
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Info Banner */}
              <div className="p-4 rounded-lg bg-orange-500/10 border border-orange-500/20">
                <p className="text-sm text-orange-200">
                  📅 Showing {slots.length} available slot
                  {slots.length !== 1 ? "s" : ""} over the next 30 days
                </p>
              </div>

              {selectionError && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-sm text-red-200">
                  {selectionError}
                </div>
              )}

              {/* Slots by Date */}
              {dates.map((date) => (
                <div key={date} className="space-y-3">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-orange-400" />
                    {date}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {slotsByDate[date].map((slot) => {
                      const isSelected = selectedSlotsSorted.some((s) => s.id === slot.id);
                      return (
                        <button
                          key={slot.id}
                          onClick={() => handleSlotClick(slot)}
                          className={`p-4 rounded-lg border transition-all text-left ${
                            isSelected
                              ? "bg-orange-500/20 border-orange-500 ring-2 ring-orange-500/50"
                              : "bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20"
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <Clock className="w-4 h-4 text-orange-400" />
                            <span className="text-sm font-semibold text-white">
                              {formatTime(slot.start_at)} - {formatTime(slot.end_at)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-white/60">
                              {formatDuration(slot.start_at, slot.end_at)}
                            </span>
                            <span className="text-sm font-bold text-white flex items-center gap-1">
                              <DollarSign className="w-3 h-3" />
                              {(slot.price_cents / 100).toFixed(2)}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer - Action Buttons */}
        {!loading && slots.length > 0 && (
          <div className="sticky bottom-0 p-6 border-t border-white/10 bg-black/50 flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 rounded-full bg-white/10 text-white font-semibold hover:bg-white/20 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!selectedSlotsSorted.length}
              className="flex-1 px-6 py-3 rounded-full bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold hover:from-orange-600 hover:to-orange-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {selectedSlotsSorted.length
                ? `Book ${selectedDurationHours} hour${selectedDurationHours !== 1 ? "s" : ""} - $${(
                    selectedTotalCents / 100
                  ).toFixed(2)}`
                : "Select Slots"}
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
}
